import { Auth } from "common";
import React, { useEffect, useState, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TamaguiProvider, Theme, YStack, Text } from "tamagui";
import config from "tamagui-config/src/tamagui.config";

import { AmplifyContext } from "./contexts/AmplifyContext";
import { AppSyncProvider } from "./contexts/AppSyncContext";
import { Dashboard } from "./screens/Dashboard";
import { Login } from "./screens/Login";

// Create a context for authentication state management
interface AuthContextType {
  isAuthenticated: boolean;
  checkAuthState: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType>({
  isAuthenticated: false,
  checkAuthState: async () => {},
});

export const useAuthContext = () => useContext(AuthContext);

export const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isInitialized } = useContext(AmplifyContext);

  const checkAuthState = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      setIsAuthenticated(!!user);
    } catch (error) {
      setIsAuthenticated(false);
      console.log("Not authenticated", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isInitialized) {
      checkAuthState();
    } else {
      console.log("Amplify not initialized yet");
    }
  }, [isInitialized, checkAuthState]);

  if (isLoading) {
    return (
      <TamaguiProvider config={config} defaultTheme="dark">
        <Theme name="dark">
          <YStack flex={1} alignItems="center" justifyContent="center">
            <Text>Loading...</Text>
          </YStack>
        </Theme>
      </TamaguiProvider>
    );
  }

  return (
    <TamaguiProvider config={config} defaultTheme="dark">
      <Theme name="dark">
        <AuthContext.Provider value={{ isAuthenticated, checkAuthState }}>
          <AppSyncProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/login"
                  element={
                    isAuthenticated ? <Navigate to="/dashboard" /> : <Login />
                  }
                />
                <Route
                  path="/"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/dashboard" />
                    ) : (
                      <Navigate to="/login" />
                    )
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    isAuthenticated ? <Dashboard /> : <Navigate to="/login" />
                  }
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </BrowserRouter>
          </AppSyncProvider>
        </AuthContext.Provider>
      </Theme>
    </TamaguiProvider>
  );
};
